import { ActionReducerMapBuilder, Draft } from '@reduxjs/toolkit'
import { filter, find, findIndex, isEmpty, map, omit, set } from 'lodash'

import {
  addObject,
  loadObjects,
  redoAction,
  removeAllObjectsInItem,
  removeObjects,
  undoAction,
  updateLabelAssistant,
  updateObject,
  UpdateObjectPayload,
  updateObjectsClass
} from '@/stores/slices/Labeling/thunks/objects'
import {
  addSnapshot,
  undoRedoObject,
  updateComments,
  updateLabelAssistantStatus,
  updateObjects
} from '@/stores/slices/Labeling/reducers/common'
import { LabelingState } from '@/stores/slices/Labeling/types'

import { LABELING_TOOL_TYPE } from '@/api/LabelingNode/const'

const _clearBackupObjects = (state: Draft<LabelingState>, itemId: string, objectId: string, rollback = true) => {
  const prev = state.backupObjects[objectId]

  if (rollback) {
    state.annotation[itemId].object = map(state.annotation[itemId].object, (o) => (o.id === prev.id ? prev : o))
  }
  state.backupObjects = omit(state.backupObjects, objectId)
}

const _optimisticUpdateObject = (state: Draft<LabelingState>, newObject: UpdateObjectPayload) => {
  const { itemId, objectId, shape, text } = newObject

  const prevObject = find(state.annotation[itemId]?.object, { id: objectId })
  if (!prevObject) {
    return
  }
  state.backupObjects = { ...state.backupObjects, [prevObject.id || '']: prevObject }

  let shapeProperties

  switch (shape) {
    case 'box': {
      const { x, y, width, height, rotation } = newObject
      shapeProperties = { x, y, width, height, rotation }
      break
    }
    case 'ellipse': {
      const { cx, cy, rx, ry, rotation } = newObject
      shapeProperties = { cx, cy, rx, ry, rotation }
      break
    }
    case 'polygon': {
      const { points } = newObject
      shapeProperties = { points }
      break
    }
    default:
      return
  }

  const optimisticObject = { ...prevObject, ...shapeProperties, toolType: LABELING_TOOL_TYPE.USER, text }
  state.annotation[itemId].object = map(state.annotation[itemId].object, (o) => (o.id === newObject.objectId ? optimisticObject : o))
}

export const objectsExtraReducers = (builder: ActionReducerMapBuilder<LabelingState>) => {
  // LOAD OBJECTS
  builder.addCase(loadObjects.pending, (state) => {
    state.isInProgress = true
  })
  builder.addCase(loadObjects.fulfilled, (state, action) => {
    const { index, isLabelAssistant = false } = action.meta.arg
    const { objectData, labelingItemId, labelingItemDetails, addSnapshot, trackedObjects } = action.payload

    state.isInProgress = false

    if (state.files.length > 0) {
      updateComments(state, labelingItemDetails)

      if (addSnapshot && objectData) {
        state.annotation[labelingItemId] = { status: labelingItemDetails.status, object: objectData }

        const fileIndex = findIndex(state.files, { _id: labelingItemId })
        if (fileIndex !== -1) {
          set(state.files, `[${fileIndex}].labelingObjects`, labelingItemDetails.labelingObjects)
        }
      }
      if (trackedObjects && !isEmpty(trackedObjects)) {
        state.annotation[labelingItemId] = { status: state.annotation[labelingItemId].status, object: trackedObjects }
        const fileIndex = findIndex(state.files, { _id: labelingItemId })
        set(state.files, `[${fileIndex}].numOfLabelObjects`, trackedObjects.length)
      }
      if (index === 0 || index === state.files.length - 1) {
        state.tool.stamp = { isTracking: false, includeText: false }
      }
      if (isLabelAssistant && index !== state.currentIndex) {
        // NOTE: segGPT 돌리는 도중에 이미지 이동되는 경우 currentIndex 업데이트 안해도됨.
        return
      }
      state.currentIndex = index
    }
  })
  builder.addCase(loadObjects.rejected, (state) => {
    state.isInProgress = false
  })

  // ADD OBJECT
  builder.addCase(addObject.fulfilled, (state, action) => {
    const { itemId, object } = action.payload

    const prevSnapshot = state.annotation[itemId]
    const newSnapshot = {
      status: prevSnapshot.status,
      object: [...prevSnapshot.object, object],
    }
    addSnapshot(state, itemId, newSnapshot)
  })

  // UPDATE OBJECT
  builder.addCase(updateObject.pending, (state, action) => _optimisticUpdateObject(state, action.meta.arg))
  builder.addCase(updateObject.fulfilled, (state, action) => _clearBackupObjects(state, action.meta.arg.itemId, action.meta.arg.objectId, false))
  builder.addCase(updateObject.rejected, (state, action) => _clearBackupObjects(state, action.meta.arg.itemId, action.meta.arg.objectId))
  builder.addCase(updateObjectsClass.fulfilled, (state, action) => updateObjects(state, action.payload.itemId, action.payload.objects))

  // DELETE OBJECT
  builder.addCase(removeObjects.fulfilled, (state, action) => {
    const { itemId, objectIds: deletedObjectIds } = action.payload

    const prevSnapshot = state.annotation[itemId]
    const filtered = filter(prevSnapshot.object, (p) => !deletedObjectIds.includes(p.id || ''))
    const newSnapshot = { status: prevSnapshot.status, object: filtered }

    addSnapshot(state, itemId, newSnapshot)
  })
  builder.addCase(removeAllObjectsInItem.fulfilled, (state, action) => {
    const { itemId } = action.payload

    const prevSnapshot = state.annotation[itemId]
    const newSnapshot = { status: prevSnapshot.status, object: [] }
    addSnapshot(state, itemId, newSnapshot)
  })

  // UPDATE LABEL ASSISTANT
  builder.addCase(updateLabelAssistant.pending, (state, action) => updateLabelAssistantStatus(state, { itemId: action.meta.arg.labelingItemId, isRunning: true }))
  builder.addCase(updateLabelAssistant.fulfilled, (state, action) => updateLabelAssistantStatus(state, { itemId: action.payload.item._id, isRunning: false }))
  builder.addCase(updateLabelAssistant.rejected, (state, action) => updateLabelAssistantStatus(state, { itemId: action.meta.arg.labelingItemId, isRunning: false }))

  // UNDO / REDO OBJECT
  builder.addCase(undoAction.fulfilled, (state, action) => undoRedoObject(state, action.payload))
  builder.addCase(redoAction.fulfilled, (state, action) => undoRedoObject(state, action.payload))
}
